import { useState } from "react";
import { FileText, GraduationCap, LayoutDashboard, Scale } from "lucide-react";

import BoardPackPanel from "./BoardPackPanel";
import ReconciliationPanel from "./ReconciliationPanel";
import ReportBuilderPanel from "./ReportBuilderPanel";

/**
 * Board reports, kept apart from the existing revenue reporting.
 *
 * One tab per business — Strategic Development and Hemisphere Education —
 * then the builder and the reconciliation view. The two dashboards run the
 * same reports, each read as its own team, so a report added once appears in
 * both. The Reporting page keeps the revenue reports exactly as they were.
 */
const TABS = [
  {
    id: "pack",
    label: "Strategic Development",
    icon: FileText,
    description:
      "Every report read as Strategic Development, in one document, ready to save as a PDF.",
    team: "corporate",
    teamLabel: "Strategic Development",
    packName: "Strategic Development Dashboard",
  },
  {
    id: "hemisphere",
    label: "Hemisphere Education",
    icon: GraduationCap,
    description:
      "The same reports read as Hemisphere Education, in one document, ready to save as a PDF.",
    team: "hemisphere_ed",
    teamLabel: "Hemisphere Education",
    packName: "Hemisphere Education Dashboard",
  },
  {
    id: "builder",
    label: "Report builder",
    icon: LayoutDashboard,
    description: "Filters, grouping, value and chart — edited here, not in a deploy.",
  },
  {
    id: "reconciliation",
    label: "Reconciliation",
    icon: Scale,
    description: "Each report's PortalIQ figure beside the HubSpot figure it replicates.",
  },
];

export default function BoardReportsPage({ portalId, portalName, fyStartMonth = 7 }) {
  const [active, setActive] = useState("pack");
  const activeTab = TABS.find((tab) => tab.id === active) || TABS[0];

  return (
    <div className="space-y-5" data-testid="board-reports-page">
      {/* Hidden in print so the pack prints as its own document. */}
      <div className="print:hidden">
        <div
          className="flex flex-wrap gap-1 border-b border-[#cccccc]"
          role="tablist"
          aria-label="Board reports"
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === active;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(tab.id)}
                className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? "border-[#57c1a9] text-[#323c44]"
                    : "border-transparent text-[#4a5b68] hover:border-[#cccccc] hover:text-[#323c44]"
                }`}
                data-testid={`board-reports-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-sm text-[#4a5b68]">{activeTab.description}</p>
      </div>

      {/* Each panel fetches on mount, so they are mounted only when shown —
          switching tabs should not fire three sets of report runs at once. */}
      {/* One panel per business. Keyed by team so switching tabs mounts a
          fresh pack rather than showing the other team's figures under this
          tab's heading while it reloads. */}
      {activeTab.team && (
        <BoardPackPanel
          key={activeTab.team}
          portalId={portalId}
          portalName={portalName}
          fyStartMonth={fyStartMonth}
          team={activeTab.team}
          teamLabel={activeTab.teamLabel}
          packName={activeTab.packName}
        />
      )}
      {active === "builder" && <ReportBuilderPanel portalId={portalId} />}
      {active === "reconciliation" && <ReconciliationPanel portalId={portalId} />}
    </div>
  );
}
